/*
  LUMECOTT Global Motion System
  Three universal functions:
  1. Button fill interaction
  2. Image scroll parallax / scroll image swap
  3. Dynamic title vine underline / keyword wrap
*/

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var parallaxItems = [];
  var ticking = false;

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function parseNumber(value, fallback) {
    var num = parseFloat(value);
    return Number.isFinite(num) ? num : fallback;
  }

  /* =========================================================
     1. Button fill interaction
     ========================================================= */

  function initButtonFill() {
    var buttonSelector = [
      '.button:not([data-lmc-no-fill])',
      'a.button:not([data-lmc-no-fill])',
      'button.button:not([data-lmc-no-fill])',
      '.product-form__submit:not([data-lmc-no-fill])',
      '.shopify-payment-button__button:not([data-lmc-no-fill])',
      '.cart__checkout-button:not([data-lmc-no-fill])',
      '.lmc-fill-button:not([data-lmc-no-fill])'
    ].join(',');

    document.querySelectorAll(buttonSelector).forEach(function (button) {
      button.classList.add('lmc-fill-ready');
    });

    document.addEventListener('click', function (event) {
      var button = event.target.closest(buttonSelector);
      if (!button) return;

      button.classList.add('lmc-is-pressed');

      window.setTimeout(function () {
        button.classList.remove('lmc-is-pressed');
      }, 170);
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches('form[action*="/cart/add"]')) return;

      var submitButton = form.querySelector('.product-form__submit, [type="submit"]');
      if (!submitButton || submitButton.hasAttribute('data-lmc-no-fill')) return;

      submitButton.classList.add('lmc-is-loading');

      window.setTimeout(function () {
        submitButton.classList.remove('lmc-is-loading');
      }, 2400);
    }, true);
  }

  /* =========================================================
     2. Image scroll parallax / scroll image swap
     ========================================================= */

  function getImageTarget(node) {
    if (!node) return null;

    if (node.tagName === 'IMG') {
      return {
        dataHost: node,
        container: node.parentElement || node,
        img: node
      };
    }

    var img = node.querySelector('img');
    if (!img) return null;

    return {
      dataHost: node,
      container: node,
      img: img
    };
  }

  function createSwapImage(container, dataHost, mainImg) {
    var swapSrc = dataHost.getAttribute('data-lmc-swap-src');
    if (!swapSrc) return null;

    if (container.querySelector('.lmc-motion-swap-img')) {
      return container.querySelector('.lmc-motion-swap-img');
    }

    var swapImg = document.createElement('img');
    swapImg.className = 'lmc-motion-swap-img';
    swapImg.src = swapSrc;
    swapImg.alt = dataHost.getAttribute('data-lmc-swap-alt') || mainImg.getAttribute('alt') || '';
    swapImg.loading = 'lazy';
    swapImg.decoding = 'async';
    swapImg.setAttribute('aria-hidden', 'true');

    container.appendChild(swapImg);

    return swapImg;
  }

  function prepareImageMotion(node) {
    if (!node || node.dataset.lmcMotionReady === 'true') return;

    var target = getImageTarget(node);
    if (!target || !target.img || !target.container) return;

    var dataHost = target.dataHost;
    var container = target.container;
    var img = target.img;
    var mode = dataHost.getAttribute('data-lmc-image-motion') || '';

    if (!mode) return;

    container.classList.add('lmc-image-motion');
    img.classList.add('lmc-motion-main-img');

    var depth = parseNumber(dataHost.getAttribute('data-lmc-depth'), -24);
    var mobileDepth = parseNumber(dataHost.getAttribute('data-lmc-mobile-depth'), depth * 0.55);
    var swapAt = parseNumber(dataHost.getAttribute('data-lmc-swap-at'), 0.55);

    if (dataHost.getAttribute('data-lmc-hover-zoom') === 'true') {
      container.setAttribute('data-lmc-hover-zoom', 'true');
    }

    var swapImg = null;

    if (mode.indexOf('swap') > -1) {
      swapImg = createSwapImage(container, dataHost, img);
    }

    parallaxItems.push({
      host: dataHost,
      container: container,
      img: img,
      swapImg: swapImg,
      mode: mode,
      depth: depth,
      mobileDepth: mobileDepth,
      swapAt: clamp(swapAt, 0.15, 0.9)
    });

    node.dataset.lmcMotionReady = 'true';
  }

  function prepareAllImageMotion() {
    parallaxItems = [];

    document.querySelectorAll('[data-lmc-image-motion]').forEach(function (node) {
      prepareImageMotion(node);
    });

    updateImageMotion();
  }

  function updateImageMotion() {
    if (prefersReducedMotion) return;

    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;

    parallaxItems.forEach(function (item) {
      if (!item.container || !item.img) return;

      var rect = item.container.getBoundingClientRect();

      if (rect.bottom < -200 || rect.top > viewportHeight + 200) return;

      var total = viewportHeight + rect.height;
      var progress = clamp((viewportHeight - rect.top) / total, 0, 1);
      var depth = isMobile ? item.mobileDepth : item.depth;
      var y = (progress - 0.5) * 2 * depth;

      if (item.mode.indexOf('parallax') > -1) {
        item.container.style.setProperty('--lmc-parallax-y', y.toFixed(2) + 'px');
      }

      if (item.mode.indexOf('swap') > -1 && item.swapImg) {
        if (progress >= item.swapAt) {
          item.container.classList.add('lmc-swap-active');
        } else {
          item.container.classList.remove('lmc-swap-active');
        }
      }
    });
  }

  function requestImageMotionUpdate() {
    if (ticking) return;

    ticking = true;

    window.requestAnimationFrame(function () {
      updateImageMotion();
      ticking = false;
    });
  }

  function bindImageMotionEvents() {
    window.addEventListener('scroll', requestImageMotionUpdate, { passive: true });
    window.addEventListener('resize', requestImageMotionUpdate);
    window.addEventListener('load', requestImageMotionUpdate);
  }

  /* =========================================================
     3. Dynamic title vine
     ========================================================= */

  function buildVineSvg() {
    var wrap = document.createElement('span');
    wrap.className = 'lmc-title-vine';
    wrap.setAttribute('aria-hidden', 'true');

    wrap.innerHTML =
      '<svg viewBox="0 0 260 32" role="presentation" focusable="false">' +
        '<path class="lmc-title-vine__stem" d="M18 18 C 55 9, 88 11, 119 16 S 184 24, 238 12" />' +
        '<path class="lmc-title-vine__curl" d="M151 17 C 162 7, 177 8, 179 18 C 181 28, 165 28, 164 18" />' +
        '<path class="lmc-title-vine__curl" d="M84 14 C 73 5, 60 7, 59 16 C 58 25, 72 25, 74 17" />' +
        '<path class="lmc-title-vine__leaf" d="M52 12 C 42 4, 31 8, 29 18 C 42 20, 51 19, 52 12 Z" />' +
        '<path class="lmc-title-vine__leaf" d="M201 14 C 211 5, 224 7, 230 16 C 216 21, 206 21, 201 14 Z" />' +
      '</svg>';

    return wrap;
  }

  function getTextAlign(element) {
    var style = window.getComputedStyle(element);
    var align = style.textAlign;

    if (align === 'left' || align === 'start') return 'left';
    if (align === 'right' || align === 'end') return 'right';

    var parent = element.parentElement;
    if (parent) {
      var parentAlign = window.getComputedStyle(parent).textAlign;
      if (parentAlign === 'left' || parentAlign === 'start') return 'left';
      if (parentAlign === 'right' || parentAlign === 'end') return 'right';
    }

    return 'center';
  }

  function applyVineSettings(element) {
    var color = element.getAttribute('data-lmc-vine-color');
    var leaf = element.getAttribute('data-lmc-vine-leaf');
    var speed = element.getAttribute('data-lmc-vine-speed') || 'standard';
    var length = element.getAttribute('data-lmc-vine-length') || 'medium';

    if (color) {
      element.style.setProperty('--lmc-vine-color-local', color);
    }

    if (leaf) {
      element.style.setProperty('--lmc-vine-leaf-local', leaf);
    }

    element.classList.add('lmc-vine-speed-' + speed);
    element.classList.add('lmc-vine-length-' + length);

    var customWidth = element.getAttribute('data-lmc-vine-width');
    if (customWidth) {
      element.style.setProperty('--lmc-vine-width-local', customWidth);
    }
  }

  function wrapFirstMatchingText(element, keyword) {
    if (!keyword) return null;

    var walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
          if (node.parentElement && node.parentElement.closest('.lmc-title-vine, .lmc-vine-word')) {
            return NodeFilter.FILTER_REJECT;
          }

          return node.nodeValue.indexOf(keyword) > -1
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    var textNode = walker.nextNode();
    if (!textNode) return null;

    var text = textNode.nodeValue;
    var index = text.indexOf(keyword);

    if (index < 0) return null;

    var before = text.slice(0, index);
    var matched = text.slice(index, index + keyword.length);
    var after = text.slice(index + keyword.length);

    var fragment = document.createDocumentFragment();

    if (before) {
      fragment.appendChild(document.createTextNode(before));
    }

    var span = document.createElement('span');
    span.className = 'lmc-vine-word';
    span.textContent = matched;
    fragment.appendChild(span);

    if (after) {
      fragment.appendChild(document.createTextNode(after));
    }

    textNode.parentNode.replaceChild(fragment, textNode);

    return span;
  }

  function prepareVine(element) {
    if (!element || element.dataset.lmcVineReady === 'true') return;

    var style = element.getAttribute('data-lmc-vine');
    if (!style || style === 'off' || style === 'false') return;

    if (
      element.closest('.product-card-wrapper') ||
      element.closest('.collection-card-wrapper') ||
      element.closest('.card__heading') ||
      element.closest('.header') ||
      element.closest('.footer') ||
      element.closest('.menu-drawer') ||
      element.closest('.cart-drawer')
    ) {
      return;
    }

    element.classList.add('lmc-vine-target');

    var align = getTextAlign(element);
    element.classList.add('lmc-vine-align-' + align);

    applyVineSettings(element);

    if (style === 'wrap') {
      var keyword = element.getAttribute('data-lmc-vine-word');
      var wordSpan = wrapFirstMatchingText(element, keyword);

      if (wordSpan) {
        wordSpan.appendChild(buildVineSvg());
      } else {
        element.appendChild(buildVineSvg());
      }
    } else {
      element.appendChild(buildVineSvg());
    }

    element.dataset.lmcVineReady = 'true';

    if (prefersReducedMotion) {
      element.classList.add('lmc-vine-visible');
    }
  }

  function prepareAllVines() {
    document.querySelectorAll('[data-lmc-vine]').forEach(function (element) {
      prepareVine(element);
    });

    observeVines();
  }

  function observeVines() {
    var vineTargets = document.querySelectorAll('.lmc-vine-target[data-lmc-vine-ready="true"]');

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      vineTargets.forEach(function (target) {
        target.classList.add('lmc-vine-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('lmc-vine-visible');
        observer.unobserve(entry.target);
      });
    }, {
      root: null,
      rootMargin: '0px 0px -16% 0px',
      threshold: 0.22
    });

    vineTargets.forEach(function (target) {
      if (!target.classList.contains('lmc-vine-visible')) {
        observer.observe(target);
      }
    });
  }

  /* =========================================================
     Init
     ========================================================= */

  function initAll() {
    initButtonFill();
    prepareAllImageMotion();
    prepareAllVines();
    requestImageMotionUpdate();
  }

  ready(function () {
    initAll();
    bindImageMotionEvents();

    document.addEventListener('shopify:section:load', function () {
      window.setTimeout(function () {
        initAll();
        requestImageMotionUpdate();
      }, 80);
    });
  });
})();